import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Form, Input, Button, Typography, Space, Tooltip, Radio, Select, notification } from 'antd';
import { PlusOutlined, CheckSquareOutlined, InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import debounce from 'lodash.debounce';
import { createChecklist } from '../../services/settingsService';
import { getChecklists } from '../../api/checklists';
import styles from './ChecklistCreateModal.module.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ChecklistCreateModal = ({ 
  visible, 
  onClose, 
  onSuccess, 
  statusId, 
  checklists = [],
  preselectedParent = null 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [characterCount, setCharacterCount] = useState(0);
  const [isSubchecklist, setIsSubchecklist] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [parentId, setParentId] = useState(preselectedParent);
  const [isNameUnique, setIsNameUnique] = useState(true);
  const [checkingUniqueness, setCheckingUniqueness] = useState(false);

  
  const checkUniqueness = useCallback(
    debounce(async (name) => {
      if (!name || name.length < 2) {
        setIsNameUnique(true);
        setCheckingUniqueness(false);
        return;
      }

      setCheckingUniqueness(true);
      
      
      const existingNames = checklists.map(cl => cl.name.toLowerCase());
      const isUnique = !existingNames.includes(name.toLowerCase());
      
      setIsNameUnique(isUnique);
      setCheckingUniqueness(false);
    }, 500),
    [checklists]
  );

  
  useEffect(() => {
    if (visible && preselectedParent) {
      setIsSubchecklist(true);
      setParentId(preselectedParent);
      form.setFieldsValue({ parentId: preselectedParent });
    }
  }, [visible, form, preselectedParent]);

  
  const handleFormChange = (changedValues, allValues) => {
    if (changedValues.name !== undefined) {
      const newName = changedValues.name || '';
      setNameValue(newName);
      setCharacterCount(newName.length);
      checkUniqueness(newName);
    }

    if (changedValues.parentId !== undefined) {
      setParentId(changedValues.parentId);
    }
  };

  
  const handleTypeChange = (value) => {
    setIsSubchecklist(value);
    if (!value) {
      setParentId(null);
      form.setFieldsValue({ parentId: undefined });
    }
  };

  
  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const checklistData = {
        name: values.name.trim(),
        ...(isSubchecklist && parentId && { parent: parentId })
      };

      const result = await createChecklist(statusId, checklistData);
      
      if (result) {
        notification.success({
          message: 'Успех',
          description: `Чеклист "${values.name}" успешно создан`,
          placement: 'bottomRight'
        });

        
        if (onSuccess) {
          const updatedChecklists = await getChecklists(statusId);
          onSuccess(updatedChecklists);
        }

        handleClose();
      }
    } catch (error) {
      console.error('Ошибка при создании чеклиста:', error);
    } finally {
      setLoading(false);
    }
  };



  
  const handleClose = () => {
    form.resetFields();
    setNameValue('');
    setCharacterCount(0);
    setIsSubchecklist(false);
    setParentId(null);
    setIsNameUnique(true);
    setCheckingUniqueness(false);
    onClose();
  };



  
  const parentOptions = useMemo(() => {
    return checklists
      .filter(cl => !cl.parent) 
      .map(cl => ({
        value: cl.id,
        label: cl.name,
        disabled: false
      }));
  }, [checklists]);

  
  const isFormValid = nameValue.length >= 2 && 
                     nameValue.length <= 250 && 
                     isNameUnique && 
                     !checkingUniqueness &&
                     (!isSubchecklist || parentId);

  return (
          <Modal
        title={
          <Space>
            <CheckSquareOutlined />
            <Title level={4} style={{ margin: 0 }}>
              {isSubchecklist ? 'Создать подчеклист' : 'Создать чеклист'}
            </Title>
          </Space>
        }
        open={visible}
        onCancel={handleClose}
        width={600}
        className={styles.createModal}
        centered
        destroyOnClose={true}
        maskClosable={false}
      footer={
        <Space>
          <Button onClick={handleClose}>
            Отмена
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={loading}
            disabled={!isFormValid}
            onClick={handleCreate}
          >
            Создать
          </Button>
        </Space>
      }
            >
        <div className={styles.modalContent}>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormChange}
          size="large"
        >
          {/* Переключатель типа */}
          <Form.Item label="Тип чеклиста">
            <Radio.Group
              value={isSubchecklist}
              onChange={(e) => handleTypeChange(e.target.value)}
              className={styles.typeSelector}
            >
              <Radio.Button value={false}>
                <CheckSquareOutlined /> Основной чеклист
              </Radio.Button>
              <Radio.Button value={true}>
                <PlusOutlined /> Подчеклист
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* Выбор родителя для подчеклиста */}
          <div className={`${styles.parentSelector} ${isSubchecklist ? styles.visible : styles.hidden}`}>
            {isSubchecklist && (
              <Form.Item
                name="parentId"
                label="Родительский чеклист"
                rules={[
                  { required: true, message: 'Выберите родительский чеклист' }
                ]}
              >
                <Select
                  placeholder="Выберите родительский чеклист"
                  options={parentOptions}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  size="large"
                  listHeight={150}
                  dropdownStyle={{ maxHeight: '150px' }}
                />
              </Form.Item>
            )}
          </div>

          {/* Поле названия */}
          <Form.Item
            name="name"
            label={
              <Space>
                Название чеклиста
                <Tooltip title="Используйте понятные и описательные названия. Избегайте слишком длинных фраз.">
                  <InfoCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            }
            rules={[
              { required: true, message: 'Введите название чеклиста' },
              { min: 2, message: 'Минимум 2 символа' },
              { max: 250, message: 'Максимум 250 символов' },
              {
                validator: () => {
                  if (!isNameUnique) {
                    return Promise.reject('Чеклист с таким названием уже существует');
                  }
                  return Promise.resolve();
                }
              }
            ]}
            validateStatus={
              checkingUniqueness ? 'validating' : 
              !isNameUnique ? 'error' : 
              nameValue.length > 0 ? 'success' : ''
            }
            hasFeedback
          >
            <TextArea
              placeholder="Введите название чеклиста..."
              autoSize={{ minRows: 2, maxRows: 4 }}
              className={styles.nameInput}
              suffix={
                <Text 
                  type={characterCount > 200 ? 'warning' : characterCount > 230 ? 'danger' : 'secondary'}
                  className={styles.characterCount}
                >
                  {characterCount}/250
                </Text>
              }
            />
          </Form.Item>

          {/* Счетчик символов */}
          <div className={styles.characterCounter}>
            <Text 
              type={characterCount > 200 ? 'warning' : characterCount > 230 ? 'danger' : 'secondary'}
            >
              {characterCount}/250 символов
            </Text>
            {characterCount > 200 && (
              <Text type="warning"> (приближается к лимиту)</Text>
            )}
          </div>


        </Form>
      </div>
    </Modal>
  );
};

export default ChecklistCreateModal; 